import React, { useCallback, useMemo, useRef, useState } from 'react';
import Button from '../../../composants/Button/Button';
import AnalyseAccordion from '../../../composants/AnalyseAccordion/AnalyseAccordion';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import { formatPrice, getStatusColor } from '../../../utils/entityUtils';
import { useLotImage } from '../../../utils/getLotImage';
import { CommandeBCI, StatusId } from '../types';
import {
  formatCommandeArticles,
  totalArticlesHT,
  buildAdminViewFormData,
  computePrestataireStatusBanner,
  isCommandeExpired,
} from '../domain/commandeConsultation';
import { envoyerOffre } from '../services/commandeBciService';
// Tableau d'articles : memes classes que la case acheteur (feuille partagee ConsultationBC.css)
// pour un rendu identique.
import '../../../composants/ConsultationBC/ConsultationBC.css';
import './CommandeOffre.css';

interface CommandeOffreProps {
  commande: CommandeBCI;
  refetch: () => Promise<void> | void;
}

// Statuts ou l'offre est deja envoyee / traitee -> plus de bouton d'envoi (lecture seule).
const SENT_OR_TERMINAL: ReadonlySet<number> = new Set<number>([
  StatusId.Valide, // 7
  StatusId.Refuse, // 8
  StatusId.Expire, // 9
  StatusId.AttenteAnalyse, // 13 (offre envoyee, attente acheteur)
  StatusId.Annule, // 14
  StatusId.PropositionFaite, // 15 (proposition transmise, en attente decision)
]);

const isValidEmail = (email: string): boolean => /^\S+@\S+\.\S+$/.test(email);

// Onglet OFFRE prestataire-BCI (V2). Le presta consulte son offre (descriptif + articles en
// lecture seule) puis l'envoie a l'acheteur via une popup ou il saisit l'email de l'acheteur
// (POST /bdci/{id}/envoyer { email }). C'est la seule action presta en BCI : pas de chiffrage
// editable, pas de signature/OTP, pas d'accept/refus cote presta.
const CommandeOffre: React.FC<CommandeOffreProps> = ({ commande, refetch }) => {
  const printRef = useRef<HTMLDivElement>(null);
  const lotImage = useLotImage(commande.operation?.lot_technique?.nom);

  const adminFormData = useMemo(() => buildAdminViewFormData(commande), [commande]);
  const articles = useMemo(() => formatCommandeArticles(commande), [commande]);
  const totalHT = useMemo(() => totalArticlesHT(articles), [articles]);
  const statusBanner = useMemo(() => computePrestataireStatusBanner(commande), [commande]);

  const op = commande.operation || {};
  const besoin = commande.formulaire?.besoin || op.besoin || 'Non défini';
  const visiteOui = op.visite === true || op.visite === 1 || op.visite === '1';
  const rang = commande.rang ?? 1;
  const sid = commande.status?.id;
  const canSend =
    sid !== undefined && !SENT_OR_TERMINAL.has(sid) && !isCommandeExpired(commande);

  const subtitle = useMemo(() => {
    const lt = op.lot_technique;
    const dep = op.lot_departement?.departement;
    return lt && dep ? `${lt.id} - ${lt.nom}, ${dep.id} - ${dep.nom}` : 'Informations de lot non disponibles';
  }, [op]);
  const dateFr = (value?: string): string => (value ? new Date(value).toLocaleDateString('fr-FR') : '-');

  // Popup d'envoi : saisie de l'email de l'acheteur.
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = useCallback(async () => {
    const value = email.trim();
    if (!value) {
      setError('Veuillez entrer un email.');
      return;
    }
    if (!isValidEmail(value)) {
      setError('Veuillez entrer un email valide.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      await envoyerOffre(commande.id, value);
      setIsSendModalOpen(false);
      setEmail('');
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'envoi de la proposition.");
    } finally {
      setSending(false);
    }
  }, [email, commande.id, refetch]);

  const printSuiviHeaderProps = {
    titre: op.nom || 'Commande BCI',
    sousTitre: '',
    subtitle,
    status: commande.status?.nom || 'Statut inconnu',
    statusColor: getStatusColor(commande.status?.nom || ''),
    delais: commande.expire_at ?? undefined,
    backgroundImage: lotImage,
  };

  return (
    <div className="consultation-bc" ref={printRef}>
      {statusBanner.visible && (
        <div className={`cmd-offre__status cmd-offre__status--${statusBanner.variant}`}>
          {statusBanner.message}
        </div>
      )}

      {/* Descriptif administratif (lecture seule) */}
      <AnalyseAccordion title="Descriptif Administratif" defaultOpen>
        <div className="section-content">
          <div className="step-container view-mode">
            <div className="form-row grid-3">
              <div className="form-group span-2">
                <label>Nom de l'opération</label>
                <div className="input-inactif">{adminFormData.nomOperation || '-'}</div>
              </div>
              <div className="form-group">
                <label>N° de référence interne</label>
                <div className="input-inactif">{adminFormData.referenceInterne || '-'}</div>
              </div>
              <div className="form-group">
                <label>Adresse de l'opération</label>
                <div className="input-inactif">{adminFormData.adresse || '-'}</div>
              </div>
              <div className="form-group">
                <label>Code postal</label>
                <div className="input-inactif">{adminFormData.codePostal || '-'}</div>
              </div>
              <div className="form-group">
                <label>Ville</label>
                <div className="input-inactif">{adminFormData.ville || '-'}</div>
              </div>
              <div className="form-group">
                <label>Date prévisionnelle de début</label>
                <div className="input-inactif">{dateFr(adminFormData.dateDebutOperation)}</div>
              </div>
              <div className="form-group">
                <label>Date prévisionnelle de fin</label>
                <div className="input-inactif">{dateFr(adminFormData.dateFinOperation)}</div>
              </div>
              <div className="form-group">
                <label>Obligation de visite :</label>
                <div className="input-inactif">{visiteOui ? 'Oui' : 'Non'}</div>
              </div>
            </div>
          </div>
        </div>
      </AnalyseAccordion>

      {/* Descriptif technique : cahier des charges read-only */}
      <AnalyseAccordion title="Descriptif Technique" defaultOpen>
        <div className="section-content">
          <div className="form-group">
            <label>Cahier des charges</label>
            <div className="input-inactif definition-besoin">{besoin}</div>
          </div>
        </div>
      </AnalyseAccordion>

      {/* Descriptif financier : articles de l'offre (lecture seule) */}
      <AnalyseAccordion title="Descriptif Financier" defaultOpen>
        <div className="section-content">
          {articles.length > 0 ? (
            <div className="articles-table-container">
              <div className="articles-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>N° Réf</th>
                      <th>Description de la prestation</th>
                      <th>Unité</th>
                      <th>Quantité</th>
                      <th>Prix unitaire HT</th>
                      <th>Total HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articles.map((article) => (
                      <tr key={article.catalogueId || article.id}>
                        <td>{article.code}</td>
                        <td>
                          <div className="article-name">{article.nom}</div>
                        </td>
                        <td>{article.unite}</td>
                        <td>{article.quantite}</td>
                        <td>{formatPrice(article.prix, 4, true)}</td>
                        <td>{formatPrice(article.totalHT, 4, true)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="articles-actions">
                <div className="total-section">
                  <p>
                    <strong>
                      Total de l'offre (Rang {rang}) : {formatPrice(totalHT, 4, true)} HT
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-articles-message">
              <p>Aucun article défini pour cette offre.</p>
            </div>
          )}
        </div>
      </AnalyseAccordion>

      {/* Actions : envoi a l'acheteur (par email) + impression */}
      <div className="cmd-offre__actions">
        <PrintButton
          targetRef={printRef}
          fileName={`${op.numero || commande.id} - Offre`}
          value="Imprimer l'offre"
          suiviHeaderProps={printSuiviHeaderProps}
        />
        {canSend && (
          <Button
            value="Envoyer à l'acheteur"
            variant="primary"
            onClick={() => {
              setError(null);
              setEmail('');
              setIsSendModalOpen(true);
            }}
          />
        )}
      </div>

      {/* Popup de choix de l'acheteur (saisie email) */}
      {isSendModalOpen && (
        <div
          className="scbci__send-overlay"
          role="presentation"
          onClick={() => !sending && setIsSendModalOpen(false)}
        >
          <div
            className="scbci__send-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Envoyer la proposition à l'acheteur"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="scbci__send-title">
              Veuillez choisir l'acheteur à qui envoyer cette proposition
            </h3>
            <label className="scbci__send-label" htmlFor="bci-acheteur-email">
              Email de l'acheteur
            </label>
            <input
              id="bci-acheteur-email"
              type="email"
              className="scbci__send-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="acheteur@exemple.fr"
              disabled={sending}
              autoFocus
            />
            {error && <p className="scbci__send-error">{error}</p>}
            <div className="scbci__send-actions">
              <Button
                value="Annuler"
                variant="cancel"
                onClick={() => setIsSendModalOpen(false)}
                disabled={sending}
              />
              <Button
                value={sending ? 'Envoi…' : 'Envoyer la proposition'}
                variant="primary"
                onClick={handleSend}
                disabled={sending}
              />
            </div>
          </div>
        </div>
      )}

      {error && !isSendModalOpen && (
        <div className="cmd-offre__inline-message cmd-offre__inline-message--error">{error}</div>
      )}
    </div>
  );
};

export default CommandeOffre;
